import type { MuiSx } from '@/shared/types';

import { useLocation } from 'react-router';
import { Box, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { Link } from '../components/Link';
import { externalLinks, footerMenu } from '../constants/Constants';
import useFeatureFlagStore from '../stores/featureflagStore';

type SiteFooterProps = {
  isSplitView?: boolean;
};

const CopyrightText = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Typography mt={2} color="text.secondary" variant="helpertext" sx={{ opacity: '.5' }}>
      © {currentYear} TheGuarantors Inc.
    </Typography>
  );
};

const splitViewStyles = {
  pt: { xs: 0, sm: 0, lg: 0 },
  pb: '20px',
} satisfies MuiSx;

const defaultStyles = {
  pt: { xs: 15, sm: 15, lg: 5 },
} satisfies MuiSx;

const SiteFooter = ({ isSplitView = false }: SiteFooterProps) => {
  const theme = useTheme();
  const location = useLocation();
  const isClaimsFormPage = location.pathname.includes('/claims/form');
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { featureFlags } = useFeatureFlagStore();

  const footerMenuLinks = featureFlags.enable_footer_trust_center
    ? footerMenu
    : footerMenu.filter((item) => item.to !== externalLinks.trustCenter);

  const sx = isSplitView ? splitViewStyles : defaultStyles;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'left', lg: 'flex-end' }}
        spacing={4}
        sx={sx}
      >
        <div style={{ maxWidth: '620px' }}>
          <Typography variant="helpertext" sx={{ opacity: '.5' }}>
            TheGuarantors is a licensed insurance agent and broker. For more detailed information,
            please see our{' '}
            <Link
              underline="always"
              data-variant="noColor"
              target="_blank"
              color={'text.secondary'}
              href={externalLinks.legalRegulatory}
              sx={{ textDecorationColor: 'grey' }}
            >
              Legal/Regulatory Notice
            </Link>{' '}
            .
          </Typography>
        </div>
        <Box sx={{ width: '100%' }}>
          <Grid
            container
            direction="row"
            alignItems={{ xs: 'none', lg: 'flex-end' }}
            justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
            columnSpacing={{ xs: 2, md: 2, lg: 4 }}
            rowSpacing={{ xs: 2, lg: 0 }}
            width={'100%'}
            minHeight="50px"
          >
            {isDesktop && <CopyrightText />}
            {footerMenuLinks.map((item, index) => {
              return (
                <Grid
                  key={index}
                  sx={{
                    [theme.breakpoints.down('lg')]: {
                      textAlign: !(index % 2) ? 'left' : 'right',
                    },
                  }}
                  size={{
                    xs: 6,
                    lg: 'auto',
                  }}
                >
                  <Link
                    variant="helpertext"
                    sx={{ opacity: '.5' }}
                    color="text.secondary"
                    target="_blank"
                    href={item.to}
                    underline="hover"
                    role="footerLinks"
                  >
                    {item.title}
                  </Link>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Stack>
      {!isSplitView && (
        <Box pb={isClaimsFormPage ? 25 : 10} pt={2}>
          {!isDesktop && <CopyrightText />}
        </Box>
      )}
    </Box>
  );
};

export default SiteFooter;
